/**
 * <PageTTSButton /> — floating text-to-speech button.
 *
 * Uses the browser's native Web Speech API (`window.speechSynthesis`).
 * Zero API cost, works offline. Picks a voice matching the user's
 * currently selected page-translate language so translated pages get
 * read in the target language, not English.
 *
 * Behaviour
 *   • Fixed bottom-right button. Play → Pause → Resume → Stop states.
 *   • Reads the main article region (`<main>`, `[role="main"]`, or the
 *     first `<article>`; falls back to `document.body` minus nav/footer).
 *   • Strips scripts, style, headers, footers, buttons, and any
 *     element with `data-tts="skip"` so nav chrome isn't spoken.
 *   • Chunks long text (~200 chars per utterance) — many browsers
 *     silently truncate SpeechSynthesisUtterance around 200-300 chars.
 *   • Hidden on browsers without SpeechSynthesis (older iOS webviews
 *     for example) so we don't render a dead button.
 */
import { useEffect, useRef, useState } from "react";
import { Volume2, Pause, Play, Square } from "lucide-react";
import { PAGE_TRANSLATE_KEY } from "@/components/PageTranslateBar";

const STATE = { IDLE: "idle", PLAYING: "playing", PAUSED: "paused" };

// Locale hints for the browser voice picker. Keys map to the same
// ISO-639-1 codes we use in PageTranslateBar.
const LANG_TAG = {
  en: "en-US", es: "es-ES", fr: "fr-FR", de: "de-DE", it: "it-IT",
  pt: "pt-PT", ru: "ru-RU", hi: "hi-IN", mr: "mr-IN", bn: "bn-IN",
  ta: "ta-IN", te: "te-IN", ml: "ml-IN", kn: "kn-IN", gu: "gu-IN",
  pa: "pa-IN", ur: "ur-PK", ar: "ar-SA", fa: "fa-IR", he: "he-IL",
  zh: "zh-CN", ja: "ja-JP", ko: "ko-KR", th: "th-TH", vi: "vi-VN",
  id: "id-ID", tr: "tr-TR", sw: "sw-KE", uk: "uk-UA", nl: "nl-NL",
  pl: "pl-PL", el: "el-GR",
};

function pickVoice(langCode) {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices || !voices.length) return null;
  const tag = LANG_TAG[langCode] || "en-US";
  // Exact locale match, then language-family match, then any voice.
  return (
    voices.find((v) => v.lang === tag) ||
    voices.find((v) => v.lang.startsWith(langCode)) ||
    voices[0]
  );
}

/**
 * Pull spoken-word text out of the current page. Skips nav / header /
 * footer / buttons / inputs / anything marked `data-tts="skip"`.
 */
function extractPageText() {
  if (typeof document === "undefined") return "";
  const root =
    document.querySelector("main") ||
    document.querySelector('[role="main"]') ||
    document.querySelector("article") ||
    document.body;
  if (!root) return "";
  const clone = root.cloneNode(true);
  clone
    .querySelectorAll(
      'header, footer, nav, aside, script, style, button, input, textarea, select, [data-tts="skip"], [aria-hidden="true"]'
    )
    .forEach((el) => el.remove());
  return (clone.innerText || clone.textContent || "")
    .replace(/\s+/g, " ")
    .trim();
}

// Split text into ~200-char sentences so long articles play reliably
// (SpeechSynthesisUtterance is capped on many browsers around 200-300).
function chunkText(text, maxLen = 200) {
  if (!text) return [];
  const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [text];
  const out = [];
  let buf = "";
  for (const s of sentences) {
    if ((buf + " " + s).length > maxLen && buf) {
      out.push(buf.trim());
      buf = s;
    } else {
      buf += " " + s;
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

export default function PageTTSButton() {
  const [state, setState] = useState(STATE.IDLE);
  const [supported, setSupported] = useState(false);
  const queueRef = useRef([]);          // remaining utterances
  const currentRef = useRef(null);      // currently-speaking utterance

  useEffect(() => {
    if (typeof window === "undefined") return;
    setSupported(!!window.speechSynthesis);
    // Voices load asynchronously in Chrome — trigger a load early.
    const w = window.speechSynthesis;
    if (w && w.getVoices().length === 0) {
      w.addEventListener?.("voiceschanged", () => {}, { once: true });
    }
    // Safety: cancel any residual utterance on unmount / route change.
    return () => {
      try { w?.cancel(); } catch {}
    };
  }, []);

  const start = () => {
    if (!supported) return;
    const w = window.speechSynthesis;
    try { w.cancel(); } catch {}

    const lang = (() => {
      try { return localStorage.getItem(PAGE_TRANSLATE_KEY) || "en"; }
      catch { return "en"; }
    })();
    const voice = pickVoice(lang);
    const langTag = LANG_TAG[lang] || "en-US";

    const chunks = chunkText(extractPageText());
    if (!chunks.length) {
      // Nothing to read — bail silently.
      return;
    }

    queueRef.current = chunks.map((text) => {
      const u = new SpeechSynthesisUtterance(text);
      if (voice) u.voice = voice;
      u.lang = langTag;
      u.rate = 1.0;
      u.pitch = 1.0;
      return u;
    });

    // Chain utterances — when one ends, kick off the next.
    const playNext = () => {
      const next = queueRef.current.shift();
      if (!next) {
        setState(STATE.IDLE);
        currentRef.current = null;
        return;
      }
      next.onend = playNext;
      next.onerror = () => {
        setState(STATE.IDLE);
        currentRef.current = null;
      };
      currentRef.current = next;
      w.speak(next);
    };

    setState(STATE.PLAYING);
    playNext();
  };

  const pause  = () => { try { window.speechSynthesis.pause();  setState(STATE.PAUSED); } catch {} };
  const resume = () => { try { window.speechSynthesis.resume(); setState(STATE.PLAYING); } catch {} };
  const stop   = () => {
    try { window.speechSynthesis.cancel(); } catch {}
    queueRef.current = [];
    currentRef.current = null;
    setState(STATE.IDLE);
  };

  if (!supported) return null;

  return (
    <div
      data-testid="page-tts-fab"
      data-tts="skip"
      className="fixed bottom-24 right-6 z-40 flex items-center gap-1.5 bg-white border border-zinc-300 shadow-lg rounded-full pl-1 pr-1 py-1"
    >
      {state === STATE.IDLE && (
        <button
          data-testid="tts-play"
          onClick={start}
          aria-label="Read this page aloud"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full hover:bg-zinc-100 text-sm font-medium text-zinc-800"
        >
          <Volume2 className="w-4 h-4" />
          <span className="hidden sm:inline">Listen</span>
        </button>
      )}

      {state === STATE.PLAYING && (
        <>
          <button
            data-testid="tts-pause"
            onClick={pause}
            aria-label="Pause reading"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full hover:bg-zinc-100 text-sm font-medium text-zinc-800"
          >
            <Pause className="w-4 h-4" />
            <span className="hidden sm:inline">Pause</span>
          </button>
          <button
            data-testid="tts-stop"
            onClick={stop}
            aria-label="Stop reading"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-zinc-100 text-zinc-600"
          >
            <Square className="w-3.5 h-3.5" />
          </button>
        </>
      )}

      {state === STATE.PAUSED && (
        <>
          <button
            data-testid="tts-resume"
            onClick={resume}
            aria-label="Resume reading"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full hover:bg-zinc-100 text-sm font-medium text-zinc-800"
          >
            <Play className="w-4 h-4" />
            <span className="hidden sm:inline">Resume</span>
          </button>
          <button
            data-testid="tts-stop-paused"
            onClick={stop}
            aria-label="Stop reading"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-zinc-100 text-zinc-600"
          >
            <Square className="w-3.5 h-3.5" />
          </button>
        </>
      )}
    </div>
  );
}
